// Minimal linear algebra — replaces glam for WebGPU terrain renderer.
// All matrices are column-major Float32Array(16), matching WGSL mat4x4<f32>.

export function mat4Identity(): Float32Array {
  const m = new Float32Array(16);
  m[0] = 1; m[5] = 1; m[10] = 1; m[15] = 1;
  return m;
}

/** Right-handed perspective projection with Z mapped to [0, 1] (WebGPU convention). */
export function mat4Perspective(fovY: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1.0 / Math.tan(fovY * 0.5);
  const rangeInv = 1.0 / (near - far);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = far * rangeInv;        // maps to [0,1]
  m[11] = -1;
  m[14] = near * far * rangeInv;
  return m;
}

/** Right-handed look-at view matrix. */
export function mat4LookAt(eye: Vec3, center: Vec3, up: Vec3): Float32Array {
  const f = vec3Normalize(vec3Sub(center, eye));
  const s = vec3Normalize(vec3Cross(f, up));
  const u = vec3Cross(s, f);
  const m = new Float32Array(16);
  m[0] = s.x;  m[1] = u.x;  m[2]  = -f.x; m[3]  = 0;
  m[4] = s.y;  m[5] = u.y;  m[6]  = -f.y; m[7]  = 0;
  m[8] = s.z;  m[9] = u.z;  m[10] = -f.z; m[11] = 0;
  m[12] = -vec3Dot(s, eye);
  m[13] = -vec3Dot(u, eye);
  m[14] = vec3Dot(f, eye);
  m[15] = 1;
  return m;
}

export function mat4Multiply(a: Float32Array, b: Float32Array): Float32Array {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      o[c * 4 + r] =
        a[0 * 4 + r] * b[c * 4 + 0] +
        a[1 * 4 + r] * b[c * 4 + 1] +
        a[2 * 4 + r] * b[c * 4 + 2] +
        a[3 * 4 + r] * b[c * 4 + 3];
    }
  }
  return o;
}

export function mat4FromTranslation(x: number, y: number, z: number): Float32Array {
  const m = mat4Identity();
  m[12] = x; m[13] = y; m[14] = z;
  return m;
}

// --- Frustum ---

/** Extract 6 frustum planes from a column-major view-projection matrix.
 *  Returns Float32Array(24): 6 planes × (nx, ny, nz, d). */
export function extractFrustumPlanes(vp: Float32Array): Float32Array {
  // vp is column-major: vp[col*4+row]
  // m[row][col] = vp[col*4+row]
  const planes = new Float32Array(24);
  // m[col][row] = vp[col*4+row]  (column-major)
  // Left:   row3+row0 of each column
  planes[0]  = vp[3] + vp[0];   planes[1]  = vp[7] + vp[4];   planes[2]  = vp[11] + vp[8];   planes[3]  = vp[15] + vp[12];
  // Right:  row3-row0
  planes[4]  = vp[3] - vp[0];   planes[5]  = vp[7] - vp[4];   planes[6]  = vp[11] - vp[8];   planes[7]  = vp[15] - vp[12];
  // Bottom: row3+row1
  planes[8]  = vp[3] + vp[1];   planes[9]  = vp[7] + vp[5];   planes[10] = vp[11] + vp[9];   planes[11] = vp[15] + vp[13];
  // Top:    row3-row1
  planes[12] = vp[3] - vp[1];   planes[13] = vp[7] - vp[5];   planes[14] = vp[11] - vp[9];   planes[15] = vp[15] - vp[13];
  // Near:   row3+row2
  planes[16] = vp[3] + vp[2];   planes[17] = vp[7] + vp[6];   planes[18] = vp[11] + vp[10];  planes[19] = vp[15] + vp[14];
  // Far:    row3-row2
  planes[20] = vp[3] - vp[2];   planes[21] = vp[7] - vp[6];   planes[22] = vp[11] - vp[10];  planes[23] = vp[15] - vp[14];

  return planes;
}

/** Test AABB against 6 frustum planes. Returns true if potentially visible. */
export function aabbVsFrustum(
  minX: number, minY: number, minZ: number,
  maxX: number, maxY: number, maxZ: number,
  planes: Float32Array,
): boolean {
  for (let i = 0; i < 6; i++) {
    const nx = planes[i * 4];
    const ny = planes[i * 4 + 1];
    const nz = planes[i * 4 + 2];
    const d  = planes[i * 4 + 3];
    const px = nx >= 0 ? maxX : minX;
    const py = ny >= 0 ? maxY : minY;
    const pz = nz >= 0 ? maxZ : minZ;
    if (nx * px + ny * py + nz * pz + d < 0) return false;
  }
  return true;
}

// --- Vec3 ---

export interface Vec3 { x: number; y: number; z: number }

export function vec3(x: number, y: number, z: number): Vec3 { return { x, y, z }; }
export function vec3Add(a: Vec3, b: Vec3): Vec3 { return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }; }
export function vec3Sub(a: Vec3, b: Vec3): Vec3 { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; }
export function vec3Scale(a: Vec3, s: number): Vec3 { return { x: a.x * s, y: a.y * s, z: a.z * s }; }
export function vec3Dot(a: Vec3, b: Vec3): number { return a.x * b.x + a.y * b.y + a.z * b.z; }
export function vec3Length(a: Vec3): number { return Math.sqrt(vec3Dot(a, a)); }
export function vec3Normalize(a: Vec3): Vec3 {
  const l = vec3Length(a);
  return l > 1e-10 ? vec3Scale(a, 1 / l) : { x: 0, y: 0, z: 0 };
}
export function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

// --- F16 conversion ---

const F16_EXPONENT_BIAS = 15;
const F32_EXPONENT_BIAS = 127;

export function f16ToF32(bits: number): number {
  const sign = (bits >>> 15) & 1;
  const exp = (bits >>> 10) & 0x1f;
  const frac = bits & 0x3ff;

  if (exp === 0) {
    if (frac === 0) return sign ? -0 : 0;
    // Subnormal
    const val = frac / 1024 * Math.pow(2, -14);
    return sign ? -val : val;
  }
  if (exp === 0x1f) {
    return frac ? NaN : (sign ? -Infinity : Infinity);
  }
  const val = Math.pow(2, exp - F16_EXPONENT_BIAS) * (1 + frac / 1024);
  return sign ? -val : val;
}

export function f32ToF16(value: number): number {
  if (isNaN(value)) return 0x7e00;
  if (!isFinite(value)) return value > 0 ? 0x7c00 : 0xfc00;
  if (value === 0) return Object.is(value, -0) ? 0x8000 : 0;

  const sign = value < 0 ? 1 : 0;
  value = Math.abs(value);

  if (value >= 65504) return sign ? 0xfc00 : 0x7c00; // overflow → infinity

  if (value < 5.96046448e-8) return sign << 15; // underflow → zero

  // Subnormal range
  if (value < 6.103515625e-5) {
    const frac = Math.round(value / Math.pow(2, -24));
    return (sign << 15) | frac;
  }

  const e = Math.floor(Math.log2(value));
  const exp = e + F16_EXPONENT_BIAS;
  const frac = Math.round((value / Math.pow(2, e) - 1) * 1024);
  return (sign << 15) | (exp << 10) | (frac & 0x3ff);
}
